import { Widget } from '../../core/Widget.js';
import { Loc } from '../../locales/registry.js';
import { keysFromSample } from '../../view/ConfigTools.js';
import { ViewWidgetHelper } from '../../view/ViewWidgetHelper.js';
import { AutocompleteCore } from './AutocompleteCore.js';
import { FreetextModeStrategy } from './modes/FreetextModeStrategy.js';
import { IndexlistModeStrategy } from './modes/IndexlistModeStrategy.js';
import { MIXED_ACTION_OPEN, MixedModeStrategy, MIXED_ITEM_USE_TEXT, MIXED_ITEM_CREATE } from './modes/MixedModeStrategy.js';
import { MultipleModeStrategy } from './modes/MultipleModeStrategy.js';
import { SingleModeStrategy } from './modes/SingleModeStrategy.js';
import { MixedActionsBar } from './MixedActionsBar.js';

/** Подстановка `{0}` в шаблон подписи. */
function formatLabel(template, value) {
    return template.replace("{0}", value);
}
class AutocompleteWidget extends Widget {
    constructor() {
        super(...arguments);
        /** Контроллер выпадающего списка. null после destroy. */
        this._core = null;
        /** Панель состояния и действий. Создаётся только в режиме `mixed`. */
        this._bar = null;
        /**
         * Счётчик запросов к провайдеру — только для событий `source-*`.
         * Защита от устаревших ответов при рендере живёт в {@link AutocompleteCore}.
         */
        this._sourceSeq = 0;
        /** Нормализация значения при `change` + делегирование в стратегию режима. */
        this._onChange = () => {
            const original = this.element.value ?? "";
            const trimmed = original.trim();
            if (trimmed !== original)
                this.element.value = trimmed;
            if (this.element.value.endsWith(",")) {
                this.element.value = this.element.value.slice(0, -1);
            }
            this._strategy.onChange();
        };
        /**
         * Терм стал короче minlength — гасим ещё не пришедший ответ,
         * чтобы он не поднял `source-response` уже после скрытия списка.
         * Сам поиск и скрытие меню выполняет ядро.
         */
        this._onInput = () => {
            const term = this._resolveTerm(this.element.value ?? "");
            if (term.length < this._opts.minlength)
                this._sourceSeq++;
        };
    }
    /**
     * Класс css для распознавания виджета.
     *
     * 2026-08-14: намеренно НЕ `im-combo`. `WidgetsMount.processElement` после монтирования
     * по `data-widget` безусловно проходит по всем зарегистрированным cssKey, а защита
     * от дублей работает по имени виджета. Общий класс с ComboBox привёл бы к тому,
     * что на элементе поднялись бы оба виджета сразу — и оба записали бы значение в форму.
     * Совместимость с потребителями (`InputCollectionWidget` и др.) решается тем,
     * что они учатся распознавать `.im-autocomplete` наравне с `.im-combo`.
     */
    static _cssKey() {
        return "im-autocomplete";
    }
    /** Схема разбора опций из DOM (`data-options` / dataset). */
    static _optionScheme() {
        return keysFromSample({
            mode: 0,
            multiple: 0,
            semicolon: 0,
            useSemicolonsSplit: 0,
            minlength: 0,
            htmlmode: 0,
            autoHtml: 0,
            sanitizeHtml: 0,
            menuclass: 0,
            appendTo: 0,
            target: 0,
            provider: 0,
            searchCmd: 0,
            outKey: 0,
            openCmd: 0,
            createCmd: 0,
            resolveCmd: 0,
            allowCreate: 0,
            allowUseAsText: 0,
            labels: 0,
        });
    }
    /** Полная инициализация (вызовется базовым классом один раз). */
    _create(options) {
        this._opts = this._normalizeOptions(options ?? {});
        // Начальный data-title = текущее визуальное значение (совместимость с ComboBox)
        if (!this.element.getAttribute("data-title")) {
            this.element.setAttribute("data-title", this.element.value ?? "");
        }
        const context = {
            element: this.element,
            semicolon: this._opts.semicolon,
            displayText: item => this._displayText(item),
            emitComboChange: detail => this._emit("combochange", detail),
            refocusCaretToEnd: () => this._refocusCaretToEnd(),
            textKey: this.element.dataset.key ?? "",
            referenceKey: this._opts.outKey,
            createRecord: text => this._createRecord(text),
            onMixedStateChanged: () => this._syncMixedBar(),
        };
        // multiple — декоратор поверх базового режима, а не отдельный режим
        const baseStrategy = this._createModeStrategy(this._opts.mode, context);
        this._strategy = this._opts.multiple
            ? new MultipleModeStrategy(context, baseStrategy)
            : baseStrategy;
        if (this._mixed) {
            this._bar = new MixedActionsBar(this.element, {
                labels: this._opts.labels,
                canOpen: !!this._opts.openCmd,
                onOpen: () => {
                    const state = this.getMixedValue();
                    if (state.type === "reference")
                        this.openCard(state.id);
                },
                onReplace: () => this.replaceValue(),
                onConvertToText: () => this.convertToText(),
                onClear: () => this.clearValue(),
            });
            this._syncMixedBar();
            // 2026-08-14: название могло не прийти вместе с GUID — доспрашиваем сервер
            if (this._opts.resolveCmd)
                this._resolveMissingDisplayName();
        }
        const provider = this._wrapProvider(this._opts.provider);
        this._core = new AutocompleteCore(this.element, {
            provider,
            minlength: this._opts.minlength,
            htmlmode: this._opts.htmlmode,
            autoHtml: this._opts.autoHtml,
            sanitizeHtml: this._opts.sanitizeHtml,
            menuclass: this._opts.menuclass,
            appendTo: this._opts.appendTo,
            target: this._opts.target,
            resolveTerm: rawValue => this._resolveTerm(rawValue),
            onSelect: (item, index) => this._strategy.onSelect(item, index),
            onItemAction: (action, item) => {
                if (action === MIXED_ACTION_OPEN)
                    this.openCard(item.key);
            },
        });
        this.element.addEventListener("change", this._onChange);
        this.element.addEventListener("input", this._onInput);
    }
    /**
     * Программный поиск/открытие списка.
     *
     * @param term Терм для автодополнения (или `"   "` — три пробела — для «показать всё»).
     */
    search(term) {
        return this._core?.search(term) ?? Promise.resolve();
    }
    /** Вернуть основное значение виджета. Форма зависит от режима. */
    getValue() {
        return this._strategy.getValue();
    }
    /**
     * Установить основное значение.
     *
     * @returns true — значение обработано; продолжать другими способами не нужно.
     */
    setValue(value) {
        return this._strategy.setValue(value);
    }
    /** Присутствует ли основное значение. */
    hasValue() {
        return this._strategy.hasValue();
    }
    /** Человекочитаемое представление значения — для чипсов фильтра и summary. */
    getValueTitle() {
        return this._strategy.getValueTitle();
    }
    /**
     * Карта значений формы.
     *
     * В `mixed` — два взаимоисключающих ключа (`data-key` и `data-out-key`).
     * В legacy-режимах карта не используется: возвращаем `undefined`,
     * чтобы `encodeValues` пошёл штатным путём через {@link getValue}.
     */
    getValues() {
        return this._mixed?.getValues();
    }
    /**
     * Установить значения картой.
     *
     * @returns false — карта не распознана; вызывающий может попробовать другой путь.
     */
    setValues(values) {
        return this._mixed?.setValues(values) ?? false;
    }
    /** Текущее состояние смешанного поля. Для legacy-режимов — `{ type: "empty" }`. */
    getMixedValue() {
        return this._mixed?.getState() ?? { type: "empty" };
    }
    /** Установить состояние смешанного поля программно. */
    setMixedValue(value) {
        this._mixed?.setState(value);
    }
    /** Преобразовать выбранную запись справочника в обычный текст. */
    convertToText() {
        this._mixed?.convertToText();
    }
    /** Очистить значение целиком. */
    clearValue() {
        this._mixed?.clear();
    }
    /**
     * Заменить выбранное значение: открыть список для выбора другой записи.
     *
     * Текущее состояние НЕ очищается — пока новая запись не выбрана,
     * в поле остаётся прежнее значение, и отмена (Escape) ничего не теряет.
     */
    replaceValue() {
        if (!this._mixed)
            return;
        this.element.focus();
        try {
            this.element.select();
        }
        catch { /* select есть не у всех типов input */ }
        void this.search("   ");
    }
    /**
     * Открыть карточку записи, НЕ меняя текущее значение поля.
     * Сервер отвечает штатными UI-командами (`openform` / `openaside`).
     */
    openCard(id) {
        const command = this._opts.openCmd;
        if (!command || !id)
            return;
        ViewWidgetHelper.ExecCmd(this._requestItem(), {
            cmd: command,
            options: { id },
        });
    }
    /** Очистка ресурсов (идемпотентно). */
    _onDestroy() {
        // Гасим ещё не пришедшие ответы провайдера, чтобы они не подняли source-события
        this._sourceSeq++;
        this.element.removeEventListener("change", this._onChange);
        this.element.removeEventListener("input", this._onInput);
        this._core?.destroy();
        this._core = null;
        this._bar?.destroy();
        this._bar = null;
    }
    /** Обновить панель действий под текущее состояние. */
    _syncMixedBar() {
        const mixed = this._mixed;
        if (!this._bar || !mixed)
            return;
        this._bar.render(mixed.getState());
    }
    /** Нормализация и валидация входных опций. */
    _normalizeOptions(options) {
        // "default" — как режим назывался в ComboBox
        const rawMode = options.mode ?? "single";
        const mode = rawMode === "default" ? "single" : rawMode;
        if (mode !== "single" && mode !== "freetext" && mode !== "indexlist" && mode !== "mixed") {
            throw new Error(`[AutocompleteWidget] unsupported mode: ${String(rawMode)}`);
        }
        if (mode === "mixed" && options.multiple) {
            throw new Error("[AutocompleteWidget] mode \"mixed\" is incompatible with multiple");
        }
        const parsedMinlength = Number(options.minlength ?? 2);
        const minlength = Number.isFinite(parsedMinlength)
            ? Math.max(0, Math.trunc(parsedMinlength))
            : 2;
        const searchCmd = options.searchCmd?.trim() || "get-autocomplete";
        const provider = options.provider ?? makeDefaultAutocompleteProvider(this.element, searchCmd);
        if (typeof provider !== "function") {
            throw new Error("[AutocompleteWidget] options.provider must be a function");
        }
        return {
            mode,
            multiple: !!options.multiple,
            // useSemicolonsSplit — то же самое поле в конфигах
            semicolon: !!(options.semicolon ?? options.useSemicolonsSplit),
            minlength,
            htmlmode: !!options.htmlmode,
            autoHtml: options.autoHtml ?? true,
            sanitizeHtml: options.sanitizeHtml ?? (html => html),
            menuclass: options.menuclass ?? "",
            appendTo: options.appendTo,
            target: options.target,
            provider,
            outKey: (options.outKey ?? this.element.dataset.outKey ?? "").trim(),
            openCmd: options.openCmd?.trim() || undefined,
            createCmd: options.createCmd?.trim() || undefined,
            resolveCmd: options.resolveCmd?.trim() || undefined,
            allowCreate: !!options.allowCreate,
            allowUseAsText: options.allowUseAsText ?? true,
            labels: {
                useAsText: options.labels?.useAsText ?? Loc.acUseAsText,
                create: options.labels?.create ?? Loc.acCreate,
                openCard: options.labels?.openCard ?? Loc.acOpenCard,
                replace: options.labels?.replace ?? Loc.acReplace,
                selectRecord: options.labels?.selectRecord ?? Loc.acSelectRecord,
                convertToText: options.labels?.convertToText ?? Loc.acConvertToText,
                clear: options.labels?.clear ?? Loc.acClear,
                referenceBadge: options.labels?.referenceBadge ?? Loc.acReferenceBadge,
                referenceNameMissing: options.labels?.referenceNameMissing ?? Loc.acReferenceNameMissing,
            },
        };
    }
    /** Создать базовую стратегию режима (без учёта multiple). */
    _createModeStrategy(mode, context) {
        switch (mode) {
            case "freetext":
                return new FreetextModeStrategy(context);
            case "indexlist":
                return new IndexlistModeStrategy(context);
            case "mixed":
                return new MixedModeStrategy(context);
            default:
                return new SingleModeStrategy(context);
        }
    }
    /** Активная стратегия mixed, либо null для legacy-режимов. */
    get _mixed() {
        return this._strategy instanceof MixedModeStrategy ? this._strategy : null;
    }
    /** Терм для поиска: в multiple — последний токен, иначе всё значение поля. */
    _resolveTerm(rawValue) {
        return this._strategy instanceof MultipleModeStrategy
            ? this._strategy.resolveTerm(rawValue)
            : rawValue;
    }
    /**
     * Обёртка провайдера, поднимающая legacy-события `source-request` / `source-response` / `source-err`.
     * События устаревших запросов подавляются по {@link _sourceSeq}.
     */
    _wrapProvider(provider) {
        return async (term) => {
            const seq = ++this._sourceSeq;
            this._emit("source-request");
            try {
                const items = await provider(term);
                if (seq === this._sourceSeq)
                    this._emit("source-response");
                return this._mixed ? this._decorateMixedItems(term, items ?? []) : items;
            }
            catch (error) {
                if (seq === this._sourceSeq)
                    this._emit("source-err");
                throw error;
            }
        };
    }
    /**
     * Текст, который должен попасть в input для выбранного пункта.
     * Приоритет: явный `item.text` → `item.value` без HTML → текст из HTML
     * (у `<img>` берётся `alt`).
     */
    _displayText(item) {
        if (typeof item.text === "string" && item.text.trim() !== "")
            return item.text.trim();
        if (!/<\/?[a-z][\s\S]*>/i.test(item.value))
            return item.value;
        const container = document.createElement("div");
        container.innerHTML = this._opts.sanitizeHtml(item.value);
        container.querySelectorAll("img").forEach(image => {
            const alt = image.getAttribute("alt")?.trim() ?? "";
            image.replaceWith(document.createTextNode(alt));
        });
        return (container.textContent ?? "").replace(/\s+/g, " ").trim();
    }
    /** Вернуть фокус в поле и поставить каретку в конец — после закрытия меню и клика мышью. */
    _refocusCaretToEnd() {
        if (this.element.disabled || this.element.readOnly)
            return;
        requestAnimationFrame(() => {
            this.element.focus();
            try {
                const end = this.element.value.length;
                this.element.setSelectionRange(end, end);
            }
            catch { /* setSelectionRange может отсутствовать на очень старых браузерах */ }
        });
    }
    /** Элемент, от имени которого уходят серверные команды (как в legacy-провайдере). */
    _requestItem() {
        return this.element.closest(".im-inputcollection") ?? this.element;
    }
    /**
     * Подмешать к найденным записям служебные пункты режима `mixed`:
     * «Использовать … как текст» и «Создать запись …», а обычным записям —
     * действие «открыть карточку».
     *
     * Действие добавляется только при заданном {@link AutocompleteOptions.openCmd}:
     * иконка, которая ничего не делает, хуже её отсутствия.
     */
    _decorateMixedItems(term, items) {
        const text = term.trim();
        const canOpen = !!this._opts.openCmd;
        const decorated = items.map(item => (canOpen && item.kind !== "separator"
            ? {
                ...item,
                actions: [
                    ...(item.actions ?? []),
                    { id: MIXED_ACTION_OPEN, icon: "new-window", title: this._opts.labels.openCard },
                ],
            }
            : item));
        // Служебные пункты бессмысленны без введённого текста
        if (!text)
            return decorated;
        if (this._opts.allowUseAsText) {
            decorated.push({
                key: "",
                value: formatLabel(this._opts.labels.useAsText, text),
                text,
                kind: MIXED_ITEM_USE_TEXT,
            });
        }
        if (this._opts.allowCreate && this._opts.createCmd) {
            decorated.push({
                key: "",
                value: formatLabel(this._opts.labels.create, text),
                text,
                kind: MIXED_ITEM_CREATE,
            });
        }
        return decorated;
    }
    /**
     * Создать запись справочника из введённого текста.
     * При успехе новая запись становится выбранным значением; при ошибке состояние не меняется.
     */
    _createRecord(text) {
        const command = this._opts.createCmd;
        const mixed = this._mixed;
        if (!command || !mixed || !text)
            return;
        ViewWidgetHelper.ExecCmd(this._requestItem(), {
            cmd: command,
            options: { text },
            onSuccess: (data) => {
                const created = readCreatedRecord(data);
                if (!created) {
                    console.error("[AutocompleteWidget] createCmd: не удалось разобрать ответ", data);
                    return;
                }
                mixed.setState({
                    type: "reference",
                    id: created.id,
                    displayName: created.displayName,
                });
                this._emit("combochange");
                this.element.dispatchEvent(new Event("change", { bubbles: true }));
            },
            onError: (error) => {
                console.error("[AutocompleteWidget] createCmd failed", error);
            },
        });
    }
    /**
     * Доспросить название записи, если сохранён только GUID.
     * Значение при неудаче не теряется: состояние остаётся `reference`,
     * в поле показывается GUID.
     */
    _resolveMissingDisplayName() {
        const command = this._opts.resolveCmd;
        const mixed = this._mixed;
        if (!command || !mixed)
            return;
        const state = mixed.getState();
        if (state.type !== "reference" || state.displayName)
            return;
        const id = state.id;
        ViewWidgetHelper.ExecCmd(this._requestItem(), {
            cmd: command,
            options: { id },
            onSuccess: (data) => {
                const resolved = readCreatedRecord(data);
                if (!resolved?.displayName)
                    return;
                // За время запроса значение могло смениться — не перетираем актуальное
                const current = mixed.getState();
                if (current.type !== "reference" || current.id !== id)
                    return;
                mixed.setState({ type: "reference", id, displayName: resolved.displayName });
            },
            onError: (error) => {
                console.error("[AutocompleteWidget] resolveCmd failed", error);
            },
        });
    }
    /** emit DOM-событий (совместимость со старым `.trigger`). */
    _emit(name, detail) {
        this.element.dispatchEvent(new CustomEvent(name, { bubbles: true, detail }));
    }
}
/** Имя виджета */
AutocompleteWidget._alias = "Autocomplete";
/**
 * Разбор ответа `createCmd` / `resolveCmd`.
 *
 * Парсер намеренно терпимый: ответ приходит нагрузкой штатной команды `callback`,
 * поэтому допускаем и обёртку, и разные регистры полей.
 *
 * @returns `{ id, displayName }` или null, если идентификатор распознать не удалось.
 */
function readCreatedRecord(data) {
    if (!data || typeof data !== "object")
        return null;
    const root = data;
    const payload = (root.item ?? root.Item ?? root.result ?? root.Result ?? root);
    if (!payload || typeof payload !== "object")
        return null;
    const id = payload.id ?? payload.Id ?? payload.ID ?? payload.key ?? payload.Key ?? payload.guid ?? payload.Guid;
    if (id == null || String(id).trim() === "")
        return null;
    const name = payload.displayName ?? payload.DisplayName ?? payload.title ?? payload.Title ??
        payload.name ?? payload.Name ?? payload.text ?? payload.Text ?? payload.value ?? payload.Value;
    const displayName = name == null ? null : String(name).trim() || null;
    return { id: String(id).trim(), displayName };
}
/**
 * Нормализация серверного пункта к {@link AcItem}.
 * Терпима к регистру и вариантам именования полей, потому что источники разные.
 *
 * @returns Нормализованный пункт или null, если распознать нечего.
 */
function normalizeAutocompleteItem(raw) {
    if (!raw || typeof raw !== "object")
        return null;
    const item = raw;
    const key = item.key ?? item.Key ?? item.id ?? item.Id ?? item.ID ??
        item.valueKey ?? item.ValueKey ?? item.value ?? item.Value;
    const value = item.value ?? item.Value ?? item.label ?? item.Label ?? item.html ??
        item.Html ?? item.text ?? item.Text ?? item.title ?? item.Title ?? item.name ?? item.Name;
    if (key == null && value == null)
        return null;
    const normalized = {
        key: String(key ?? value ?? ""),
        value: String(value ?? key ?? ""),
    };
    const text = item.text ?? item.Text ?? item.plain ?? item.Plain;
    const url = item.url ?? item.Url ?? item.href ?? item.Href;
    if (text != null)
        normalized.text = String(text);
    // url — legacy-паритет с ComboBox: переход по ссылке при выборе (см. LegacyModeStrategy)
    if (url != null)
        normalized.url = String(url);
    return normalized;
}
/**
 * Фабрика провайдера, работающего через штатный серверный вызов.
 *
 * Как и в legacy: команда отправляется от ближайшего `.im-inputcollection`,
 * а если его нет — от самого элемента.
 *
 * @param element Инпут/textarea виджета
 * @param command Имя серверной команды
 * @returns provider(term) → Promise<AcItem[]>
 */
function makeDefaultAutocompleteProvider(element, command = "get-autocomplete") {
    return term => new Promise((resolve, reject) => {
        const requestItem = element.closest(".im-inputcollection") ?? element;
        ViewWidgetHelper.ExecCmd(requestItem, {
            cmd: command,
            options: { term },
            onSuccess: (data) => {
                try {
                    const response = data;
                    const raw = response?.items ?? response?.Items ?? data ?? [];
                    const values = Array.isArray(raw) ? raw : [];
                    resolve(values.map(normalizeAutocompleteItem).filter((item) => item !== null));
                }
                catch (error) {
                    reject(error);
                }
            },
            onError: (error) => reject(error ?? new Error(`${command} failed`)),
        });
    });
}

export { AutocompleteWidget, makeDefaultAutocompleteProvider, normalizeAutocompleteItem, readCreatedRecord };
